import {
  BulkDraftCreationResult,
  CreateDraftDto,
  OrderLineUpdateDto,
  OrderType,
  OrderUpdateDto,
  OriginApp,
  type SubmitDraftItemDto,
  type UpdateDraftItemDto,
  type ValidatorResult,
} from '@onrunning/backstage-models';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useFormat } from '@/composables/format';
import { useNotification } from '@/composables/useNotification';
import { OrderEditType } from '@/constants/orderEditType';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { OrderAction } from '@/models/order-action.enum';
import { RouteNames } from '@/router';
import { DraftsService } from '@/services/drafts.service';
import { OrdersService } from '@/services/orders.service';
import { useAccountStore } from '@/stores/account';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { Draft } from '@/types/draft.interface';
import type { MatrixOrder } from '@/types/matrix.interface';
import type { EditableOrder, Order, OrderEntity } from '@/types/order.interface';
import { useFabricValidation } from './useFabricValidation';
import { useMatrixProduct } from './useMatrixProduct';
import { useNewRelic } from './useNewRelic';

export interface PartialSubmitResult {
  succeededOrderNumbers: string[];
  failedItems: Array<{
    draftId: string;
    draftNumber: string;
    dueDate?: string;
    errors: string[];
  }>;
}

export interface UseOrderManagement {
  contactId?: Ref<string | undefined>;
  editType?: OrderEditType;
}

export function useOrderManagement(options?: UseOrderManagement) {
  const userStore = useUserStore();
  const orderStore = useOrderStore();
  const accountStore = useAccountStore();
  const productMatrixStore = useProductMatrixStore();
  const { matrixType } = useMatrixProduct();
  const newrelic = useNewRelic();

  const fabricValidations = useFabricValidation(matrixType);

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const { notifyError } = useNotification();
  const { formatNonLocalisedDateString, formatError } = useFormat();

  // Services
  const ordersService = new OrdersService();
  const draftsService = new DraftsService();

  const saving = ref(false);
  const deleting = ref(false);
  const submitting = ref(false);
  const showProductsConfirmationModal = ref(false);
  const partialSubmitResult = ref<PartialSubmitResult | null>(null);
  const processingMessage = ref('');

  const isOrderEditMode = computed(() => options?.editType === OrderEditType.Order);

  const isMultipleOrder = computed(() => orderStore.orderType === OrderType.MultiplePreOrder);

  const seasonStartDate = computed((): string => {
    return orderStore.orderTypeInfo?.startDate as string;
  });

  const orders = computed(() => {
    return new Map([...orderStore.orders, ...orderStore.updatedOrders]);
  });

  const seasonEndDate = computed((): string => {
    return orderStore.orderTypeInfo?.endDate as string;
  });

  const onSaveDraft = async () => {
    // Prevent duplicate draft creation from rapid clicks
    if (saving.value) {
      return;
    }

    saving.value = true;

    await upsertDrafts();
    saving.value = false;
    await router.push('/orders');
  };

  const deleteDraft = async (index: number) => {
    const draft = productMatrixStore.matrixOrders[index];

    if (draft?.orderId && draft.orderNumber) {
      await draftsService.remove(draft.orderId, userStore.selectedAccount?.id);
      orderStore.deleteOrder(draft.orderNumber);
    }

    productMatrixStore.deleteMatrixOrder(index);

    if (!productMatrixStore.matrixOrders.length) {
      return router.push('/orders');
    }
  };

  const deleteAllDraftsOnExit = async () => {
    deleting.value = true;

    try {
      await Promise.all(
        productMatrixStore.matrixOrders.map(async (draft) => {
          if (draft?.orderId) {
            await draftsService.remove(draft.orderId, userStore.selectedAccount?.id);
          }
        }),
      );
    } catch (err) {
      notifyError(t('backend.orders.delete_draft_error'));
    } finally {
      deleting.value = false;
      router.push('/orders');
    }
  };

  const onSubmitOrders = async () => {
    // Prevent double submission from rapid clicks
    if (submitting.value) {
      return;
    }

    if (accountStore.isOnHold) {
      onSaveDraft();

      return;
    }

    submitting.value = true;

    let confirmedOrderNumbers: string[] | undefined;

    if (isOrderEditMode.value) {
      confirmedOrderNumbers = await updateExistingOrder();
    } else {
      processingMessage.value = t('global.submitting_orders');
      confirmedOrderNumbers = await submitDraft();
    }

    submitting.value = false;
    processingMessage.value = '';

    if (partialSubmitResult.value) {
      return;
    }

    const confirmedOrderNumber = confirmedOrderNumbers?.join(', ');

    if (confirmedOrderNumber) {
      router.replace({
        name: RouteNames.SuccessOrdered,
        params: { orderType: orderStore.orderType, orderNumber: confirmedOrderNumber },
        query: { orderAction: isOrderEditMode.value ? OrderAction.Update : OrderAction.Create },
      });
    }
  };

  const onProceedToOrderDetails = async (autoProceeded: boolean) => {
    // Prevent duplicate draft creation from rapid clicks
    if (saving.value) {
      return;
    }

    try {
      if (autoProceeded) showProductsConfirmationModal.value = false;

      saving.value = true;

      if (isOrderEditMode.value) {
        const matrixOrder = productMatrixStore.matrixOrders[0];
        const order = orders.value.get(matrixOrder.orderNumber as string);
        const payload = {
          ...order,
          products: buildUpdateOrderProducts(matrixOrder),
        } as EditableOrder;

        orderStore.setUpdatedOrder(payload);
        orderStore.setUpdateStatus(productMatrixStore.matrixUpdated);
      } else {
        const promises = await upsertDrafts();

        promises?.forEach((draft, index) => {
          if (!draft) return;
          orderStore.setOrder(draft);
          productMatrixStore.updateMatrixOrder({ orderId: draft.id, orderNumber: draft.orderNumber }, index);
        });

        productMatrixStore.setUpdateStatus(false);
      }

      const firstMatrixOrder = productMatrixStore.matrixOrders[0];
      if (firstMatrixOrder.orderNumber?.toLocaleLowerCase()?.includes('draft')) {
        const path = `/orders/${firstMatrixOrder.orderId}/edit-products/${OrderEditType.Draft}/${AddEditOrderBreadcrumbSteps.orderDetails}`;
        router.push(path);

        return;
      }

      router.push({
        params: {
          ...route.params,
          step: AddEditOrderBreadcrumbSteps.orderDetails,
        },
      });
    } catch (error) {
      newrelic.noticeError(error as Error, {
        message: 'onProceedToOrderDetails failed',
      });
    } finally {
      saving.value = false;
      processingMessage.value = '';
      showProductsConfirmationModal.value = false;
    }
  };

  const buildCreateDraftLines = (matrixOrder: MatrixOrder) => {
    const draftProducts = [];

    for (const productId in matrixOrder.matrixProducts) {
      const product = productMatrixStore.products[productId];
      if (!product) {
        continue;
      }

      const disabledReason = fabricValidations.getFabricDisabledReason(
        product.launchDate,
        matrixOrder.date,
        seasonStartDate.value,
      );

      if (!disabledReason) {
        const matrixProduct = matrixOrder.matrixProducts[productId];

        if (matrixProduct.quantity) {
          draftProducts.push({
            barcode: product.productId,
            quantity: matrixProduct.quantity,
            productGroupName: product.productGroupName,
            productName: `${product.productName} ${product.sizeId}`,
            itemNumber: product.productGroupNumber,
          });
        }
      }
    }

    return draftProducts;
  };

  const buildUpdateDraftProducts = (matrixOrder: MatrixOrder) => {
    const draftProducts = [];

    for (const productId in matrixOrder.matrixProducts) {
      const product = productMatrixStore.products[productId];
      if (!product) {
        continue;
      }

      const disabledReason = fabricValidations.getFabricDisabledReason(
        product.launchDate,
        matrixOrder.date,
        seasonStartDate.value,
      );

      if (!disabledReason) {
        const matrixProduct = matrixOrder.matrixProducts[productId];

        if (matrixProduct.quantity) {
          draftProducts.push({
            quantity: matrixProduct.quantity,
            productId: matrixProduct.productId ?? product.productId,
            name: matrixProduct.productGroupNumber ?? product.productGroupNumber,
            productGroupName: matrixProduct.productGroupName ?? product.productGroupName,
            description: `${product.productName} ${product.sizeId}`,
          });
        }
      }
    }

    return draftProducts;
  };

  const buildUpdateOrderProducts = (matrixOrder: MatrixOrder) => {
    const orderProducts = [];

    for (const productId in matrixOrder.matrixProducts) {
      const product = productMatrixStore.products[productId];

      if (!product) {
        continue;
      }

      const disabledReason = fabricValidations.getFabricDisabledReason(
        product.launchDate,
        matrixOrder.date,
        seasonStartDate.value,
      );

      if (!disabledReason) {
        const matrixProduct = matrixOrder.matrixProducts[productId];

        if (matrixProduct.quantity) {
          orderProducts.push({
            name: product.productGroupNumber,
            productSizeId: product.sizeId,
            productId: product.productId,
            quantity: matrixProduct.quantity,
            productGroupName: product.productGroupName,
            description: `${product.productName} ${product.sizeId}`,
          });
        }
      }
    }

    return orderProducts;
  };

  const throwIfBulkItemErrors = (
    results: { errors?: { error: string }[] }[],
    errorLabel: string,
    fallbackMessage: string,
    newRelicContext?: Record<string, string | undefined>,
  ) => {
    const itemErrors = results.filter((result) => result.errors?.length);

    if (itemErrors.length > 0) {
      const uniqueErrors = [...new Set(itemErrors.flatMap((item) => item.errors ?? []).map((detail) => detail.error))];
      const errorMessage = uniqueErrors.map((error) => t(error)).join(', ');
      newrelic.noticeError(new Error(errorLabel), newRelicContext);

      throw new Error(errorMessage || fallbackMessage);
    }
  };

  const assertBulkResultValid = (
    result: ValidatorResult<BulkDraftCreationResult>,
    errorLabel: string,
    fallbackMessage: string,
    newRelicContext?: Record<string, string | undefined>,
  ) => {
    if (!result.isValid()) {
      newrelic.noticeError(new Error(errorLabel), newRelicContext);
      throw new Error(formatError(result.errors) || fallbackMessage);
    }
  };

  const createNewDrafts = async (matrixOrders: MatrixOrder[], groupId?: string): Promise<Draft[]> => {
    const bulkDtos = matrixOrders.map(
      (matrixOrder): CreateDraftDto => ({
        origin: OriginApp.Backstage,
        contactId:
          resolveContactId(options?.contactId?.value) ||
          (userStore.isASalesRep ? orderStore.orders.values().next().value?.contactId : undefined),
        type: orderStore.orderType as OrderType,
        requestedDate: formatNonLocalisedDateString(matrixOrder.date),
        isSalesRep: userStore.isASalesRep,
        groupId,
        lines: buildCreateDraftLines(matrixOrder),
        customerPoNumber: matrixOrder.customerPoNumber,
        isCreatedViaExcelUpload: matrixOrder.isCreatedViaExcelUpload ?? false,
      }),
    );

    processingMessage.value = t('global.saving_orders');
    const bulkResult = await draftsService.createDraftsBulk(bulkDtos, userStore.selectedAccount?.id);

    assertBulkResultValid(bulkResult, 'Bulk draft creation failed', t('global.error_creating_draft'), { groupId });

    throwIfBulkItemErrors(
      bulkResult.model.results,
      'Bulk draft creation partial failure',
      t('global.error_creating_draft'),
      { groupId },
    );

    // DraftModel → Draft requires double cast because their OrderStatus enums diverge (local enum lacks ReadyToSubmit)
    return bulkResult.model.results.map((draftResult) => draftResult.draft as unknown as Draft);
  };

  const updateExistingDrafts = async (matrixOrders: MatrixOrder[]): Promise<Draft[]> => {
    const bulkUpdateDtos: UpdateDraftItemDto[] = matrixOrders.map((matrixOrder) => {
      const draft = orders.value.get(matrixOrder.orderNumber as string) as Draft;

      return {
        id: draft.id,
        contactId: resolveContactId(draft.contactId),
        customerPoNumber: draft.customerPoNumber,
        deliveryAddress: draft.deliveryAddress,
        dueDate: formatNonLocalisedDateString(matrixOrder.date),
        products: buildUpdateDraftProducts(matrixOrder),
      } as UpdateDraftItemDto;
    });

    processingMessage.value = t('global.saving_orders');
    const bulkUpdateResult = await draftsService.updateDraftsBulk(bulkUpdateDtos, userStore.selectedAccount?.id);

    assertBulkResultValid(bulkUpdateResult, 'Bulk draft update failed', t('global.error_saving_draft'));

    throwIfBulkItemErrors(
      bulkUpdateResult.model.results,
      'Bulk draft update partial failure',
      t('global.error_saving_draft'),
    );

    // Merge the update payloads back onto the original drafts
    return matrixOrders
      .map((matrixOrder) => {
        const draft = orders.value.get(matrixOrder.orderNumber as string);
        const dto = bulkUpdateDtos.find((bulkUpdateItem) => bulkUpdateItem.id === draft?.id);

        if (!draft || !dto) {
          return undefined;
        }

        return { ...draft, ...dto };
      })
      .filter(Boolean) as Draft[];
  };

  const upsertDrafts = async () => {
    try {
      let groupId: string | undefined;

      if (isMultipleOrder.value) {
        groupId = orderStore.orders.values().next().value?.groupId || uuidv4();
      }

      const newMatrixOrders = productMatrixStore.matrixOrders.filter((matrixOrder) => !matrixOrder.orderId);
      const existingMatrixOrders = productMatrixStore.matrixOrders.filter((matrixOrder) => !!matrixOrder.orderId);

      const createdDrafts = newMatrixOrders.length > 0 ? await createNewDrafts(newMatrixOrders, groupId) : [];
      const updatedDrafts = existingMatrixOrders.length > 0 ? await updateExistingDrafts(existingMatrixOrders) : [];

      return productMatrixStore.matrixOrders.map((matrixOrder) => {
        if (matrixOrder.orderId) {
          return updatedDrafts.find((draftItem) => draftItem.orderNumber === matrixOrder.orderNumber);
        }

        // Safe positional lookup via reference equality: newMatrixOrders holds the same object
        // references as productMatrixStore.matrixOrders (filtered, not cloned), and the array is
        // not mutated between filter and map. New drafts have no orderNumber/id before creation,
        // so there's no shared key to match by — positional alignment is the only option.
        const index = newMatrixOrders.indexOf(matrixOrder);

        return index >= 0 ? createdDrafts[index] : undefined;
      });
    } catch (error: any) {
      notifyError(error?.message || t('backend.orders.save_draft_error'));

      throw error;
    } finally {
      processingMessage.value = '';
    }
  };

  const updateDraft = async (draft: Draft, matrixOrder: MatrixOrder): Promise<Draft> => {
    const updatedDraft = {
      id: draft.id,
      orderNumber: draft.orderNumber,
      contactId: resolveContactId(draft.contactId),
      accountId: userStore.selectedAccount.id,
      userId: userStore.userProfile.user.id,
      type: orderStore.orderType,
      customerPoNumber: draft.customerPoNumber,
      deliveryAddress: draft.deliveryAddress,
      dueDate: formatNonLocalisedDateString(matrixOrder.date),
      products: buildUpdateDraftProducts(matrixOrder),
    } as any;

    const result = await draftsService.update(updatedDraft, userStore.selectedAccount?.id);

    if (!result.isValid()) {
      newrelic.noticeError(new Error('Draft update failed'), {
        accountNumber: updatedDraft.accountId,
        userId: updatedDraft.userId,
        contactId: updatedDraft.contactId,
      });
      throw new Error(formatError(result.errors) || t('global.error_saving_draft'));
    }

    return { ...draft, ...updatedDraft } as Draft;
  };

  const updateExistingOrder = async (): Promise<string[] | undefined> => {
    const updates = Array.from(orderStore.updatedOrders.values()) as EditableOrder[];
    try {
      return await Promise.all(
        updates.map(async (order: EditableOrder) => {
          const payload = {
            orderNumber: order.orderNumber,
            contactId: resolveContactId(order.contactId),
            deliveryAddress: order.deliveryAddress,
            customerPoNumber: order.customerPoNumber,
            lines: order.products.map((product) => ({
              barcode: product.productId,
              quantity: product.quantity,
            })) as OrderLineUpdateDto[],
          } as OrderUpdateDto;

          const result = await ordersService.updateOrder(payload, userStore.selectedAccount?.id);

          if (!result.isValid()) {
            throw new Error(formatError(result.errors) || t('global.error_updating_order'));
          }

          return order.orderNumber;
        }),
      );
    } catch (err: any) {
      notifyError(err?.message || t('global.error_updating_order'));
    }
  };

  const submitDraft = async (): Promise<string[] | undefined> => {
    const drafts = Array.from(orders.value.values()) as Draft[];

    try {
      const payloads: SubmitDraftItemDto[] = drafts.map((draft: Draft) => ({
        id: draft.id,
        dueDate: draft.dueDate,
        contactId: resolveContactId(draft.contactId),
        customerPoNumber: draft.customerPoNumber,
        deliveryAddress: draft.deliveryAddress as SubmitDraftItemDto['deliveryAddress'],
        shippingSettings: draft.shippingSettings,
      }));

      const result = await draftsService.submitDraftsBulk(payloads, userStore.selectedAccount?.id);

      if (!result.isValid()) {
        throw new Error(formatError(result.errors) || t('global.error_submitting_draft'));
      }

      const succeededItems = result.model.results.filter((item) => item.orderNumber && !item.errors?.length);
      const failedItems = result.model.results.filter((item) => item.errors?.length);

      if (failedItems.length > 0 && succeededItems.length > 0) {
        partialSubmitResult.value = {
          succeededOrderNumbers: succeededItems.map((item) => item.orderNumber!),
          failedItems: failedItems.map((item) => ({
            draftId: item.draftId ?? '',
            draftNumber: drafts.find((draft) => draft.id === item.draftId)?.orderNumber ?? item.draftId ?? '',
            dueDate: payloads.find((payload) => payload.id === item.draftId)?.dueDate,
            errors: (item.errors ?? []).map((error) => {
              const key = error.error;

              if (!key) {
                return t('global.error');
              }

              const translated = t(key);

              return translated === key ? key : translated;
            }),
          })),
        };

        return undefined;
      }

      if (failedItems.length > 0) {
        const errorMessage = failedItems
          .flatMap((item) => item.errors ?? [])
          .map((error) => error.error)
          .join(', ');
        throw new Error(errorMessage || t('global.error_submitting_draft'));
      }

      return result.model.results.map((resultItem) => resultItem.orderNumber!);
    } catch (err: any) {
      notifyError(err?.message || t('global.error_submitting_draft'));
    }
  };

  const getOrders = async (orderId: string, isDraft = false): Promise<Draft[] | EditableOrder[] | Order[]> => {
    const order = await fetchOrder(orderId, isDraft);

    let relatedOrders: Draft[] | EditableOrder[] | Order[] = [];

    if (order?.relatedOrders?.length) {
      relatedOrders = await loadRelatedOrders(order.relatedOrders, order.id, isDraft);
    }

    return [order, ...relatedOrders] as Draft[] | EditableOrder[] | Order[];
  };

  const fetchOrder = async (id: string, isDraft: boolean = false): Promise<OrderEntity> => {
    if (isDraft) {
      const result: ValidatorResult<any> = await draftsService.get(id, userStore.selectedAccount?.id, true);
      if (!result.isValid()) {
        throw new Error(formatError(result.errors) || t('orders.fetch_draft_error'));
      }

      return result.model as Draft;
    } else {
      const result: ValidatorResult<any> = await ordersService.get(id, userStore.selectedAccount?.id);
      if (!result.isValid()) {
        throw new Error(formatError(result.errors) || t('orders.fetch_order_error'));
      }

      return result.model as EditableOrder | Order;
    }
  };

  const loadRelatedOrders = async (
    relatedOrders: { id: string }[],
    mainOrderId: string,
    isDraft: boolean,
  ): Promise<Draft[] | EditableOrder[] | Order[]> => {
    const requests = [];

    for (const order of relatedOrders) {
      if (order.id !== mainOrderId) {
        requests.push(fetchOrder(order.id, isDraft));
      }
    }

    return Promise.all(requests) as Promise<Draft[] | EditableOrder[] | Order[]>;
  };

  const resolveContactId = (contactId?: string): string | undefined => {
    if (userStore.isADealer) {
      return undefined;
    }

    if (contactId?.trim()) {
      return contactId.trim();
    }

    return undefined;
  };

  return {
    submitting,
    saving,
    deleting,
    showProductsConfirmationModal,
    partialSubmitResult,
    processingMessage,
    onSaveDraft,
    updateDraft,
    deleteDraft,
    deleteAllDraftsOnExit,
    onSubmitOrders,
    onProceedToOrderDetails,
    seasonStartDate,
    seasonEndDate,
    getOrders,
  };
}
